import React, { useState, useEffect, useRef } from 'react';
import { Form, Select, Input, InputNumber, Row, Col, Card, Button, Spin, message, Space, type SelectProps, Typography } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { LoadingOutlined, SearchOutlined, CloseOutlined, ArrowLeftOutlined, AppstoreOutlined, BorderOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import { type } from 'os';
import { theme } from '../../theme';

const { useMessage } = message;

const { Option } = Select;
const { Title } = Typography;

interface Producto {
  id: number;
  descripcion: string;
  tipo: number;
  nivel: string;
}

interface TipoCosecha {
  id: number;
  descripcion: string;
}

interface VariedadSemilla {
  id: number;
  descripcion: string;
}

interface Presentacion {
  id: number;
  descripcion: string;
}

interface Sucursal {
  id: number;
  denominacion: string;
}

interface Padron {
  id: number;
  nc: string;
  denominacion: string;
}

interface Persona {
  id_padron: number;
  nc: string;
  denominacion: string;
}

interface Establecimiento {
  id: number;
  descripcion: string;
}

interface Lote {
  id: number;
  descripcion: string;
}

interface Chofer {
  id: number;
  nombre: string;
  vencimiento_carnet: string;
  vencimiento_art: string;
}

interface Camion {
  id: number;
  patente_camion: string;
  patente_acoplado: string;
}

interface PesajeForm {
  id_empresa: number;
  motivo: string;
  sucursal?: number;
  producto: number;
  tipo_cosecha?: number;
  tipo_semilla?: number;
  presentacion?: number;
  cliente: number;
  productor?: number;
  establecimiento?: number;
  lote?: number;
  transportista: number;
  chofer: number;
  camion: number;
  tarifa: number;
  humedad?: number;
  cantidad_rollos?: number;
  ubicacion_playa?: string;
  calificacion?: string;
  remito?: string;
}

interface User {
  id_empresa: number;
  id_sucursal: number;
}

interface DatosFijos {
  id: number;
  id_empresa: number;
  id_sucursal: number;
  valor_string: string;
  alta: number;
}

interface CeldaResponse {
  casillero: string;
  error?: string;
}

const Pesaje: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  const [messageApi, contextHolder] = message.useMessage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [tiposCosecha, setTiposCosecha] = useState<TipoCosecha[]>([]);
  const [loading, setLoading] = useState(true);
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;
  console.log('empresa',id_empresa);
  console.log('sucursal',id_sucursal);

  // Estados para los selects
  const [motivos, setMotivos] = useState<any[]>([]);
  const [tiposSemilla, setTiposSemilla] = useState<VariedadSemilla[]>([]);
  const [presentaciones, setPresentaciones] = useState<Presentacion[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [clientes, setClientes] = useState<Padron[]>([]);
  const [productores, setProductores] = useState<Padron[]>([]);
  const [transportistas, setTransportistas] = useState<Padron[]>([]);
  const [establecimientos, setEstablecimientos] = useState<Establecimiento[]>([]);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [choferes, setChoferes] = useState<Chofer[]>([]);
  const [camiones, setCamiones] = useState<Camion[]>([]);
  // Este valor debería venir de tu contexto o configuración
  // Agregar un estado para controlar el tipo de producto
  const [tipoProducto, setTipoProducto] = useState<number | null>(null);

  // Agregar estado de carga
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mover los estados al inicio del componente
  const [estadoCalificacion, setEstadoCalificacion] = useState<boolean>(false);
  const [calificacionEnabled, setCalificacionEnabled] = useState<boolean>(false);
  const [tipoCosechaEnabled, setTipoCosechaEnabled] = useState<boolean>(false);
  const [tipoSemillaEnabled, setTipoSemillaEnabled] = useState<boolean>(false);
  const [presentacionEnabled, setPresentacionEnabled] = useState<boolean>(false);

  // Agregar el nuevo estado para playa
  const [playa, setPlaya] = useState<DatosFijos[]>([]);

  // Estados para búsqueda de personas
  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Persona | null>(null);
  const [searchProductor, setSearchProductor] = useState('');
  const [showSearchProductor, setShowSearchProductor] = useState(false);
  const [selectedProductor, setSelectedProductor] = useState<Persona | null>(null);
  const [searchTransportista, setSearchTransportista] = useState('');
  const [showSearchTransportista, setShowSearchTransportista] = useState(false);
  const [selectedTransportista, setSelectedTransportista] = useState<Persona | null>(null);
  
  // Estado para número de columnas (3 o 4)
  const [numColumnas, setNumColumnas] = useState<number>(() => {
    const saved = localStorage.getItem('nuevo_pesaje_columnas');
    return saved ? Number(saved) : 4; // Por defecto 4 columnas
  });

  // Función para cambiar número de columnas
  const toggleColumnas = () => {
    const nuevoNum = numColumnas === 3 ? 4 : 3;
    setNumColumnas(nuevoNum);
    localStorage.setItem('nuevo_pesaje_columnas', nuevoNum.toString());
  };

  // Función para verificar el estado de calificación
  const verificarEstadoCalificacion = async () => {
    try {
      if (!user) {
        console.error('No hay usuario autenticado');
        return false;
      }

      const userMetadata = user as unknown as User;
      const id_empresa = userMetadata.id_empresa;
      const id_sucursal = userMetadata.id_sucursal;

      const token = await getAccessTokenSilently();
      const response = await apiClient.get<DatosFijos[]>(
        `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/PRE`,
        { token }
      );

      // Verificar si hay datos y alta = 1
      const tieneEstadoActivo = response.data && 
                               response.data.length > 0 && 
                               response.data[0].alta === 1;

      console.log('Estado Calificación:', tieneEstadoActivo ? 'Activo' : 'Inactivo');
      return tieneEstadoActivo;

    } catch (error) {
      console.error('Error al verificar estado de calificación:', error);
      return false;
    }
  };

  // Efecto para cargar productos
  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        console.log('Usuario no autenticado, esperando...');
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        if (!token) {
          console.error('No se pudo obtener el token de autenticación');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/productos`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

      if (!response.ok) {
        if (response.status === 403) {
          const errorText = await response.text().catch(() => '');
          console.error('Error 403: No tiene permisos para acceder a este recurso', errorText);
          messageApi.error('No tiene permisos para acceder a este recurso');
        } else {
          const errorText = await response.text().catch(() => '');
          console.error(`Error ${response.status} al cargar productos:`, errorText);
          messageApi.error(`Error al cargar productos: ${response.status}`);
        }
        return;
      }

        const data = await response.json();
        setProductos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        if (error instanceof Error) {
          messageApi.error(`Error al cargar productos: ${error.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, user]);

  // Efecto para cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-dropdown')) {
        setShowSearchCliente(false);
        setShowSearchProductor(false);
        setShowSearchTransportista(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Efecto para cargar tipos de cosecha
  const fetchTiposCosecha = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/tipo_cosecha`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar tipos de cosecha');
      const data = await response.json();
      setTiposCosecha(data);
    } catch (error) {
      console.error('Error al cargar tipos de cosecha:', error);
      messageApi.error('Error al cargar tipos de cosecha');
    }
  };

  // Función para cargar variedades de semilla
  const fetchVariedadesSemilla = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/variedad_semilla`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar variedades de semilla');
      const data = await response.json();
      setTiposSemilla(data);
    } catch (error) {
      console.error('Error al cargar variedades de semilla:', error);
      messageApi.error('Error al cargar variedades de semilla');
    }
  };

  // Función para cargar presentaciones
  const fetchPresentaciones = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/presentacion`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar presentaciones');
      const data = await response.json();
      setPresentaciones(data);
    } catch (error) {
      console.error('Error al cargar presentaciones:', error);
      messageApi.error('Error al cargar presentaciones');
    }
  };

  // Función para cargar sucursales actualizada
  const fetchSucursales = async () => {
    try {
      const token = await getAccessTokenSilently();
      console.log('Cargando sucursales...'); // Debug
      console.log('ID de la empresa:', id_empresa); // Debug
      const ruta=`${process.env.REACT_APP_SUCURSAL_URL}/sucursales/${id_empresa}`
      console.log('Ruta:', ruta); // Debug
      const response = await fetch(ruta, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar sucursales');
      }

      const data = await response.json();
      console.log('Sucursales cargadas:', data); // Debug
      setSucursales(data);
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
    }
  };

  // Función para cargar datos del padrón
  const fetchPadron = async () => {
    try {
      if (!user) {
        console.log('Usuario no autenticado, esperando...');
        return;
      }

      if (!id_empresa) {
        console.log('id_empresa no está disponible aún');
        return;
      }

      console.log('=== INICIANDO CARGA DEL PADRÓN ===');
      console.log('ID Empresa:', id_empresa);
      console.log('Tipo de ID Empresa:', typeof id_empresa);

      let token;
      try {
        token = await getAccessTokenSilently();
        if (!token) {
          console.error('No se pudo obtener el token de autenticación');
          return;
        }
      } catch (tokenError) {
        console.error('Error al obtener token:', tokenError);
        return;
      }

      console.log('Token obtenido:', token ? 'SÍ' : 'NO');

      if (!process.env.REACT_APP_PADRON_URL) {
        console.error('REACT_APP_PADRON_URL no está configurada');
        return;
      }

      const urlUsuarios = `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}/`;
      console.log('URL de usuarios:', urlUsuarios);
      console.log('Variable de entorno REACT_APP_PADRON_URL:', process.env.REACT_APP_PADRON_URL);

      let responseUsuarios;
      try {
        responseUsuarios = await fetch(urlUsuarios, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (fetchError) {
        console.error('Error de red al hacer fetch:', fetchError);
        if (fetchError instanceof TypeError && fetchError.message === 'Failed to fetch') {
          console.error('Posible problema de CORS o servidor no disponible');
          console.error('Verificar que el servidor backend esté corriendo en:', process.env.REACT_APP_PADRON_URL);
        }
        throw fetchError;
      }

      console.log('Status de la respuesta:', responseUsuarios.status);
      console.log('Headers de la respuesta:', responseUsuarios.headers);

      if (responseUsuarios.ok) {
        const dataUsuarios = await responseUsuarios.json();
        console.log('Datos recibidos del padrón:', dataUsuarios);
        
        // Validar que los datos sean un array
        const padronData = Array.isArray(dataUsuarios) ? dataUsuarios : [];
        console.log('Cantidad de registros recibidos:', padronData.length);
        
        // Asignar los datos directamente a los estados
        setClientes(padronData);
        setProductores(padronData);
        setTransportistas(padronData);
        
        console.log('Datos asignados exitosamente a los estados');
        console.log('Estado clientes actualizado:', padronData.length, 'registros');
        console.log('Estado productores actualizado:', padronData.length, 'registros');
        console.log('Estado transportistas actualizado:', padronData.length, 'registros');
      } else {
        console.error('Error al cargar el padrón - Status:', responseUsuarios.status);
        console.error('Error al cargar el padrón - Status Text:', responseUsuarios.statusText);
        
        // Intentar obtener el mensaje de error
        try {
          const errorData = await responseUsuarios.text();
          console.error('Respuesta de error:', errorData);
                        messageApi.error(`Error al cargar el padrón: ${responseUsuarios.status} ${responseUsuarios.statusText}`);
        } catch (e) {
          console.error('No se pudo leer la respuesta de error');
                        messageApi.error('Error al cargar el padrón');
        }
      }
    } catch (error) {
      console.error('Error en fetchPadron:', error);
      console.error('Tipo de error:', typeof error);
      if (error instanceof Error) {
        console.error('Mensaje de error:', error.message);
        console.error('Stack trace:', error.stack);
        
        // Mostrar mensaje de error al usuario
        if (error.message === 'Failed to fetch') {
                    messageApi.error('No se pudo conectar con el servidor. Verifica que el backend esté corriendo.');
        } else {
                    messageApi.error(`Error al cargar el padrón: ${error.message}`);
        }
      } else {
        messageApi.error('Error desconocido al cargar el padrón');
      }
    }
  };

  // Efecto para cargar datos al inicio
  useEffect(() => {
    if (user && id_empresa) {
      fetchPadron();
    }
  }, [user, id_empresa, getAccessTokenSilently]);

  // Función para cargar establecimientos
  const fetchEstablecimientos = async (productorId: number) => {
    try {
      const token = await getAccessTokenSilently();
      console.log('Cargando establecimientos...'); // Debug
      const ruta = `${process.env.REACT_APP_BALANZA_URL}/establecimiento/${id_empresa}/${productorId}`;
      console.log('Ruta:', ruta); // Debug
      const response = await fetch(ruta, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar establecimientos');
      }

      const data = await response.json();
      console.log('Establecimientos cargados:', data); // Debug
      setEstablecimientos(data);
    } catch (error) {
      console.error('Error al cargar establecimientos:', error);
    }
  };

  // Función para cargar lotes
  const fetchLotes = async (productorId: number, establecimientoId: number) => {
    try {
      const token = await getAccessTokenSilently();
      console.log('Cargando lotes...'); // Debug
      console.log('ID Productor:', productorId); // Debug
      console.log('ID Establecimiento:', establecimientoId); // Debug
      
      const ruta = `${process.env.REACT_APP_BALANZA_URL}/lote/${id_empresa}/${establecimientoId}/`;
      console.log('Ruta:', ruta); // Debug

      const response = await fetch(ruta, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar lotes: ${response.status}`);
      }

      const data = await response.json();
      console.log('Lotes cargados:', data); // Debug
      setLotes(data);
    } catch (error) {
      console.error('Error al cargar lotes:', error);
      setLotes([]); // Limpiar lotes en caso de error
    }
  };

  // Función para cargar choferes
  const fetchChoferes = async (transportistaId: number) => {
    try {
      const token = await getAccessTokenSilently();
      console.log('Cargando choferes...'); // Debug
      console.log('ID Empresa:', id_empresa); // Debug
      console.log('ID Transportista:', transportistaId); // Debug
      
      const ruta = `${process.env.REACT_APP_BALANZA_URL}/chofer/${id_empresa}/${transportistaId}/`;
      console.log('Ruta choferes:', ruta); // Debug

      const response = await fetch(ruta, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar choferes: ${response.status}`);
      }

      const data = await response.json();
      console.log('Choferes cargados:', data); // Debug
      setChoferes(data);
    } catch (error) {
      console.error('Error al cargar choferes:', error);
      setChoferes([]); // Limpiar choferes en caso de error
    }
  };

  // Función para cargar camiones
  const fetchCamiones = async (transportistaId: number) => {
    try {
      const token = await getAccessTokenSilently();
      console.log('Cargando camiones...'); // Debug
      console.log('ID Empresa:', id_empresa); // Debug
      console.log('ID Transportista:', transportistaId); // Debug
      
      const ruta = `${process.env.REACT_APP_BALANZA_URL}/camion/${id_empresa}/${transportistaId}/`;
      console.log('Ruta camiones:', ruta); // Debug

      const response = await fetch(ruta, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar camiones: ${response.status}`);
      }

      const data = await response.json();
      console.log('Camiones cargados:', data); // Debug
      setCamiones(data);
    } catch (error) {
      console.error('Error al cargar camiones:', error);
      setCamiones([]); // Limpiar camiones en caso de error
    }
  };

  // Función para manejar el cambio de producto
  const handleProductoChange = async (value: number) => {
    try {
      console.log('Iniciando handleProductoChange con valor:', value);
      const productoSeleccionado = productos.find(p => p.id === value);
      console.log('Producto seleccionado:', productoSeleccionado);
      
      // Por defecto, todo deshabilitado
      setCalificacionEnabled(false);
      setTipoCosechaEnabled(false);
      setTipoSemillaEnabled(false);
      setPresentacionEnabled(false);
      
      if (productoSeleccionado) {
        // Si es tipo 1, verificar EC y cargar datos adicionales
        if (productoSeleccionado.tipo === 1) {
          console.log('Producto es tipo 1, verificando EC');
          const ec = await verificarEstadoCalificacion();
          setEstadoCalificacion(ec);
          console.log('Estado de calificación (EC):', ec);

          // Cargar datos independientemente del EC
          console.log('Cargando datos adicionales...');
          await Promise.all([
            fetchTiposCosecha(),
            fetchVariedadesSemilla(),
            fetchPresentaciones()
          ]);
          console.log('Datos adicionales cargados');

          // Habilitar campos solo si EC es true
          if (ec) {
            console.log('EC es true, habilitando campos');
            setCalificacionEnabled(true);
            setTipoCosechaEnabled(true);
            setTipoSemillaEnabled(true);
            setPresentacionEnabled(true);
          } else {
            console.log('EC es false, limpiando campos');
            form.setFieldsValue({
              calificacion: undefined,
              tipo_cosecha: undefined,
              tipo_semilla: undefined,
              presentacion: undefined
            });
          }
        } else {
          console.log('Producto no es tipo 1, limpiando campos');
          setEstadoCalificacion(false);
          form.setFieldsValue({
            calificacion: undefined,
            tipo_cosecha: undefined,
            tipo_semilla: undefined,
            presentacion: undefined
          });
        }
      }

    } catch (error) {
      console.error('Error en handleProductoChange:', error);
      messageApi.error('Error al procesar cambio de producto');
      setEstadoCalificacion(false);
      setCalificacionEnabled(false);
      setTipoCosechaEnabled(false);
      setTipoSemillaEnabled(false);
      setPresentacionEnabled(false);
    }
  };

  // Función para manejar el cambio de motivo
  const handleMotivoChange = (value: string) => {
    // Limpiar campos dependientes cuando cambia el motivo
    form.setFieldsValue({
      sucursal: undefined,
      cliente: undefined,
      productor: undefined,
      transportista: undefined,
      chofer: undefined,
      camion: undefined
    });

    // Cargar sucursales solo para motivos 'A' y 'F'
    if (value === 'F' || value === 'A') {
      fetchSucursales();
    }

    // Cargar padrón para motivo 'A' (A Otra Sucursal)
    if (value === 'A') {
      fetchPadron();
    }
  };

  // Función para manejar el cambio de productor
  const handleProductorChange = (value: number) => {
    // Limpiar establecimiento cuando cambia el productor
    form.setFieldValue('establecimiento', undefined);
    // Cargar establecimientos del nuevo productor
    if (value) {
      fetchEstablecimientos(value);
    }
  };

  // Función para manejar el cambio de establecimiento
  const handleEstablecimientoChange = (value: number) => {
    // Limpiar lote cuando cambia el establecimiento
    form.setFieldValue('lote', undefined);
    
    // Cargar lotes del nuevo establecimiento
    const productorId = form.getFieldValue('productor');
    if (value && productorId) {
      fetchLotes(productorId, value);
    }
  };

  // Función para manejar el cambio de transportista
  const handleTransportistaChange = (value: number) => {
    // Limpiar campos dependientes
    form.setFieldsValue({
      chofer: undefined,
      camion: undefined
    });

    // Si hay un transportista seleccionado, cargar sus choferes y camiones
    if (value) {
      fetchChoferes(value);
      fetchCamiones(value);
    } else {
      setChoferes([]);
      setCamiones([]);
    }
  };

  // Agregar la función para obtener los datos de playa
  const fetchDatosPlaya = async () => {
    try {
      if (!user) {
        console.error('No hay usuario autenticado');
        return;
      }

      if (!id_empresa || !id_sucursal) {
        console.log('id_empresa o id_sucursal no están disponibles aún');
        return;
      }

      let token;
      try {
        token = await getAccessTokenSilently();
        if (!token) {
          console.error('No se pudo obtener el token de autenticación');
          return;
        }
      } catch (tokenError) {
        console.error('Error al obtener token:', tokenError);
        return;
      }

      const response = await apiClient.get<DatosFijos[]>(
        `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/PMR`,
        { token }
      );

      setPlaya(Array.isArray(response.data) ? response.data : []);
      console.log('Datos Playa:', response.data);

    } catch (error: any) {
      console.error('Error al obtener datos de playa:', error);
      if (error?.response?.status === 403) {
        console.error('Error 403: No tiene permisos para acceder a datos de playa');
        // No mostrar mensaje de error para evitar spam, solo log
      } else {
        messageApi.error('Error al cargar datos de playa');
      }
    }
  };

  // Agregar el useEffect para cargar los datos al inicio
  useEffect(() => {
    if (id_empresa && id_sucursal) {
      fetchDatosPlaya();
    }
  }, [getAccessTokenSilently, id_empresa, id_sucursal]);

  // Agregar animación de entrada al montar el componente
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.classList.add('slide-in-right');
      // Remover la clase después de la animación
      const timer = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('slide-in-right');
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  // Modificar la función verificarEstadoPlaya para incluir todas las separaciones
  const verificarEstadoPlaya = () => {
    if (playa && playa.length > 0) {
      if (playa[0].alta === 1) {
        const dato_playa = playa[0].valor_string;
        
        // Separar primero por el signo '='
        const [dato_playa_columnas, dato_playa_filas] = dato_playa.split('=').map(item => item.trim());
        
        // Separar columnas por el signo '-'
        const [desde_columna, hasta_columna] = dato_playa_columnas.split('-').map(item => item.trim());
        
        // Separar filas por el signo '-'
        const [desde_linea, hasta_linea] = dato_playa_filas.split('-').map(item => item.trim());
        
        // Mostrar en consola para control
        console.log('dato_playa original:', dato_playa);
        console.log('Columnas:', {
          desde_columna,
          hasta_columna
        });
        console.log('Filas:', {
          desde_linea,
          hasta_linea
        });
        
        return true;
      }
      return false;
    }
    return false;
  };

  // Modificar la función buscarCeldaDesocupada
  const buscarCeldaDesocupada = async (clienteId: number) => {
    try {
      if (!verificarEstadoPlaya()) {
        return;
      }

      const [dato_playa_columnas, dato_playa_filas] = playa[0].valor_string.split('=').map(item => item.trim());
      const [desde_columna, hasta_columna] = dato_playa_columnas.split('-').map(item => item.trim());
      const [desde_linea, hasta_linea] = dato_playa_filas.split('-').map(item => item.trim());

      const token = await getAccessTokenSilently();
      const response = await apiClient.get<CeldaResponse>(
        `${process.env.REACT_APP_RELACION_URL}/celdadesocupada/${clienteId}/${desde_linea}/${hasta_linea}/${desde_columna}/${hasta_columna}/${id_empresa}/${id_sucursal}`,
        { token }
      );

      // Ahora TypeScript conoce el tipo de response.data
      const casillero = response.data.casillero;
      console.log('Casillero encontrado:', casillero);
      
      // Establecer el valor extraído en el campo ubicacion_playa
      form.setFieldValue('ubicacion_playa', casillero);
      return casillero;

    } catch (error) {
      console.error('Error al buscar celda desocupada:', error);
      messageApi.error('Error al buscar ubicación en playa disponible');
      return null;
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" tip="Cargando datos..." />
        </div>
      </Card>
    );
  }

  // Función para manejar el envío del formulario
  const onFinish = async (values: any) => {
    setIsSubmitting(true); // Iniciar carga
    try {
      const token = await getAccessTokenSilently();
      console.log('Valores del formulario:', values); // Debug

      const pesajeData: PesajeForm = {
        id_empresa,
        id_sucursal,
        ...values,
        // Convertir valores numéricos si es necesario
        tarifa: Number(values.tarifa),
        humedad: values.humedad ? Number(values.humedad) : undefined,
        cantidad_rollos: values.cantidad_rollos ? Number(values.cantidad_rollos) : undefined,
      };

      console.log('Data a enviar:', pesajeData); // Debug

      const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/crear_pesaje/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pesajeData),
      });

      if (!response.ok) {
        throw new Error(`Error al crear pesaje: ${response.status}`);
      }

      const data = await response.json();
      console.log('Respuesta del servidor:', data); // Debug

      // Mostrar mensaje de éxito
      messageApi.success('Pesaje creado exitosamente');
      
      // Resetear la pantalla después de grabar exitosamente
      form.resetFields();
      // Limpiar los estados
      setChoferes([]);
      setCamiones([]);
      setSucursales([]);
      setEstablecimientos([]);
      setLotes([]);
      setTiposCosecha([]);
      setTiposSemilla([]);
      setPresentaciones([]);
      // Limpiar selecciones de personas
      setSelectedCliente(null);
      setSearchCliente('');
      setSelectedProductor(null);
      setSearchProductor('');
      setSelectedTransportista(null);
      setSearchTransportista('');
      // Resetear otros estados
      setTipoProducto(null);
      setEstadoCalificacion(false);
      setCalificacionEnabled(false);
      setTipoCosechaEnabled(false);
      setTipoSemillaEnabled(false);
      setPresentacionEnabled(false);

    } catch (error) {
      console.error('Error al crear pesaje:', error);
      messageApi.error('Error al crear el pesaje');
    } finally {
      setIsSubmitting(false); // Finalizar carga
    }
  };

  return (
    <>
      {contextHolder}
      <div ref={containerRef} className="page-transition-container" style={{ padding: '0', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <style>{`
        .page-transition-container {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
          transform: translateX(0);
          opacity: 1;
        }
        .page-transition-container.slide-out-left {
          transform: translateX(-100%);
          opacity: 0;
        }
        .page-transition-container.slide-in-right {
          animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {/* Título de la página con botón de volver y toggle de columnas */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: '12px', 
        marginBottom: '12px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/listar_pesaje')}
            size="small"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.colors.borderLight}`,
              borderRadius: '6px',
              height: '32px',
              fontSize: '13px'
            }}
          >
            Volver
          </Button>
          <Title 
            level={3} 
            style={{ 
              margin: 0,
              color: theme.colors.primaryDark, 
              fontSize: '20px', 
              fontWeight: 600,
              lineHeight: '1.2'
            }}
          >
            Nuevo Pesaje
          </Title>
        </div>
        <Button
          icon={numColumnas === 4 ? <AppstoreOutlined /> : <BorderOutlined />}
          onClick={toggleColumnas}
          size="small"
          type={numColumnas === 4 ? 'primary' : 'default'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '32px',
            fontSize: '13px',
            minWidth: '100px'
          }}
          title={`Cambiar a ${numColumnas === 4 ? '3' : '4'} columnas`}
        >
          {numColumnas === 4 ? '4 Columnas' : '3 Columnas'}
        </Button>
      </div>

      <Card
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '1400px'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '13px',
            fontWeight: 600,
            padding: '8px 16px',
            minHeight: 'auto'
          }
        }}
        title="Información del Pesaje"
      >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ 
          maxWidth: '100%',
          padding: '12px'
        }}
        className="pesaje-form"
      >
        <style>{`
          .pesaje-form .ant-form-item-label > label {
            color: #374151 !important;
            font-weight: 500 !important;
            font-size: 12px !important;
          }
          .pesaje-form .ant-form-item-required::before {
            color: ${theme.colors.error} !important;
          }
          .pesaje-form .ant-input,
          .pesaje-form .ant-select-selector,
          .pesaje-form .ant-input-number {
            border-color: #d1d5db !important;
            border-radius: 6px !important;
            width: 100% !important;
            font-size: 13px !important;
          }
          .pesaje-form .ant-input:focus,
          .pesaje-form .ant-select-selector:focus,
          .pesaje-form .ant-input-number:focus {
            border-color: ${theme.colors.primary} !important;
            box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
          }
          .pesaje-form .ant-form-item {
            margin-bottom: 0 !important;
          }
          .pesaje-form .ant-select {
            width: 100% !important;
          }
          .pesaje-form .ant-input-number {
            width: 100% !important;
          }
          .pesaje-grid {
            display: grid;
            gap: 12px;
          }
          .pesaje-grid.columns-4 {
            grid-template-columns: repeat(4, 1fr);
          }
          .pesaje-grid.columns-3 {
            grid-template-columns: repeat(3, 1fr);
          }
          @media (max-width: 1200px) {
            .pesaje-grid.columns-4 {
              grid-template-columns: repeat(3, 1fr);
            }
            .pesaje-grid.columns-3 {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (max-width: 768px) {
            .pesaje-grid.columns-4,
            .pesaje-grid.columns-3 {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        
        {/* Grid responsive - 3 o 4 columnas según preferencia del usuario */}
        <div className={`pesaje-grid ${numColumnas === 4 ? 'columns-4' : 'columns-3'}`}>
          
          {/* Tipo de Movimiento */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Tipo de Movimiento:
              </label>
              <Form.Item
                name="tipo_movimiento"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <Select 
                  placeholder="Seleccione tipo"
                  size="middle"
                  onChange={(value) => {
                    // Limpiar el motivo cuando cambia el tipo de movimiento
                    form.setFieldValue('motivo', undefined);
                  }}
                >
                  <Option value="I">Ingreso</Option>
                  <Option value="E">Egreso</Option>
                </Select>
              </Form.Item>
            </div>

          {/* Motivo */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.tipo_movimiento !== currentValues.tipo_movimiento
              }
            >
              {({ getFieldValue }) => 
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ 
                    fontWeight: 600, 
                    color: theme.colors.textPrimary,
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    marginBottom: 0,
                    lineHeight: '1.2'
                  }}>
                    Motivo:
                  </label>
                  <Form.Item
                    name="motivo"
                    rules={[{ required: true, message: 'Campo requerido' }]}
                    style={{ marginBottom: 0, width: '100%' }}
                  >
                    <Select 
                      placeholder="Seleccione motivo"
                      size="middle"
                      showSearch
                      optionFilterProp="children"
                      disabled={!getFieldValue('tipo_movimiento')}
                      onChange={handleMotivoChange}
                    >
                    {getFieldValue('tipo_movimiento') === 'I' ? (
                      <>
                        <Option value="C">Compra</Option>
                        <Option value="S">Servicio</Option>
                        <Option value="R">Recupero</Option>
                        <Option value="F">Desde Otra Sucursal</Option>
                      </>
                    ) : (
                      <>
                        <Option value="V">Venta</Option>
                        <Option value="A">A Otra Sucursal</Option>
                        <Option value="D">Cambio Dueño</Option>
                        <Option value="E">Retiro Cliente</Option>
                      </>
                    )}
                  </Select>
                  </Form.Item>
                </div>
              }
            </Form.Item>

          {/* Producto */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Producto:
              </label>
              <Form.Item
                name="producto"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <Select 
                  placeholder="Seleccione producto"
                  size="middle"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleProductoChange}
                  filterOption={(input: string, option?: { children: string; value: number }) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                {productos.map(producto => (
                  <Option key={producto.id} value={producto.id}>
                    {producto.descripcion}
                  </Option>
                ))}
              </Select>
              </Form.Item>
            </div>

          {/* Tipo de Cosecha */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.producto !== currentValues.producto
              }
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find(p => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Tipo de Cosecha:
                    </label>
                    <Form.Item
                      name="tipo_cosecha"
                      rules={[{ required: camposHabilitados, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <Select 
                        placeholder={camposHabilitados ? "Seleccione tipo de cosecha" : "No disponible"}
                        size="middle"
                        disabled={!camposHabilitados}
                        optionFilterProp="children"
                      >
                      {tiposCosecha.map(tipo => (
                        <Option key={tipo.id} value={tipo.id}>
                          {tipo.descripcion}
                        </Option>
                      ))}
                    </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Tipo Semilla */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.producto !== currentValues.producto
              }
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find(p => p.id === getFieldValue('producto'));
                const semillaHabilitado = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Tipo Semilla:
                    </label>
                    <Form.Item
                      name="tipo_semilla"
                      rules={[{ required: semillaHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <Select 
                        placeholder={semillaHabilitado ? "Seleccione tipo de semilla" : "No disponible"}
                        size="middle"
                        disabled={!semillaHabilitado}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                      {semillaHabilitado && tiposSemilla.map(tipo => (
                        <Option key={tipo.id} value={tipo.id}>
                          {tipo.descripcion}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Presentación */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.producto !== currentValues.producto
              }
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find(p => p.id === getFieldValue('producto'));
                const presentacionHabilitado = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Presentación:
                    </label>
                    <Form.Item
                      name="presentacion"
                      rules={[{ required: presentacionHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <Select 
                        placeholder={presentacionHabilitado ? "Seleccione presentación" : "No disponible"}
                        size="middle"
                        disabled={!presentacionHabilitado}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                      {presentacionHabilitado && presentaciones.map(presentacion => (
                        <Option key={presentacion.id} value={presentacion.id}>
                          {presentacion.descripcion}
                        </Option>
                      ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* DTV */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                DTV:
              </label>
              <Form.Item
                name="dtv"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <Input placeholder="Ingrese DTV" size="large" maxLength={50} />
              </Form.Item>
            </div>

          {/* Sucursal */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.motivo !== currentValues.motivo
              }
            >
              {({ getFieldValue }) => {
                const motivoSeleccionado = getFieldValue('motivo');
                const sucursalHabilitado = motivoSeleccionado === 'A' || motivoSeleccionado === 'F';

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Sucursal:
                    </label>
                    <Form.Item
                      name="sucursal"
                      rules={[{ required: sucursalHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <Select 
                        placeholder={sucursalHabilitado ? "Seleccione sucursal" : "No disponible"}
                        size="middle"
                        showSearch
                        disabled={!sucursalHabilitado}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                      {sucursalHabilitado && sucursales
                        .filter(suc => {
                          // Para motivo 'A' (A Otra Sucursal), no mostrar la sucursal actual
                          if (motivoSeleccionado === 'A') {
                            return suc.id !== user?.id_sucursal;
                          }
                          // Para otros motivos, mostrar todas las sucursales
                          return true;
                        })
                        .map(sucursal => (
                          <Option key={sucursal.id} value={sucursal.id}>
                            {sucursal.denominacion}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Cliente */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.motivo !== currentValues.motivo
              }
            >
              {({ getFieldValue }) => {
                const motivoSeleccionado = getFieldValue('motivo');
                const clienteHabilitado = motivoSeleccionado !== 'A';

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Cliente:
                    </label>
                    <Form.Item
                      name="cliente"
                      rules={[{ required: clienteHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type="text"
                  placeholder={clienteHabilitado ? "Buscar por NC o denominación..." : "No disponible"}
                  required={clienteHabilitado}
                  disabled={!clienteHabilitado}
                  style={{
                    width: '100%',
                          paddingLeft: '28px',
                          paddingRight: selectedCliente ? '28px' : undefined,
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    fontSize: '13px',
                    height: '32px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    backgroundColor: selectedCliente ? '#f5f5f5' : (!clienteHabilitado ? '#f0f0f0' : 'white'),
                    cursor: clienteHabilitado ? 'pointer' : 'not-allowed'
                  }}
                  value={selectedCliente ? `${selectedCliente.nc} - ${selectedCliente.denominacion}` : searchCliente}
                  onChange={e => {
                    setSearchCliente(e.target.value);
                    setSelectedCliente(null);
                    setShowSearchCliente(true);
                  }}
                  onFocus={() => setShowSearchCliente(true)}
                />
                      {/* Lupa */}
                      <SearchOutlined style={{
                        position: 'absolute',
                        left: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        pointerEvents: 'none',
                        fontSize: '13px'
                      }} />
                      {/* Limpiar selección */}
                      {selectedCliente && (
                        <CloseOutlined
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#666',
                            cursor: 'pointer',
                            fontSize: '13px',
                            zIndex: 1100
                          }}
                          onClick={() => {
                            setSelectedCliente(null);
                            setSearchCliente('');
                            setShowSearchCliente(true);
                            form.setFieldValue('cliente', undefined);
                          }}
                          title="Limpiar selección"
                        />
                      )}
                {/* Dropdown */}
                {showSearchCliente && clientes.length > 0 && !selectedCliente && (
                  <div 
                    className="search-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: '400px',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {clientes.filter(c => {
                      const search = searchCliente.trim().toLowerCase();
                      return (
                        c.nc.toLowerCase().includes(search) ||
                        c.denominacion.toLowerCase().includes(search)
                      );
                    }).map(cliente => (
                      <div
                        key={cliente.id}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          textAlign: 'left'
                        }}
                        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                        onClick={async () => {
                          const personaCliente: Persona = {
                            id_padron: cliente.id,
                            nc: cliente.nc,
                            denominacion: cliente.denominacion
                          };
                          setSelectedCliente(personaCliente);
                          form.setFieldValue('cliente', cliente.id);
                          setShowSearchCliente(false);
                          if (verificarEstadoPlaya()) {
                            await buscarCeldaDesocupada(cliente.id);
                          }
                        }}
                      >
                        {cliente.nc} - {cliente.denominacion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
                  </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Productor */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.producto !== currentValues.producto ||
                prevValues.tipo_cosecha !== currentValues.tipo_cosecha
              }
            >
              {({ getFieldValue }) => {
                const productoId = getFieldValue('producto');
                const tipoCosecha = getFieldValue('tipo_cosecha');
                const productoSeleccionado = productos.find(p => p.id === productoId);
                const productorHabilitado = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Productor:
                    </label>
                    <Form.Item
                      name="productor"
                      rules={[{ required: productorHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input
                        type="text"
                        placeholder={productorHabilitado ? "Buscar por NC o denominación..." : "No disponible"}
                        required={productorHabilitado}
                        disabled={!productoId || productoSeleccionado?.tipo !== 1}
                        style={{
                          width: '100%',
                          paddingLeft: '30px',
                          paddingRight: selectedProductor ? '30px' : undefined,
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          backgroundColor: selectedProductor ? '#f5f5f5' : 'white',
                          cursor: (!productoId || productoSeleccionado?.tipo !== 1) ? 'not-allowed' : 'pointer'
                        }}
                        value={selectedProductor ? `${selectedProductor.nc} - ${selectedProductor.denominacion}` : searchProductor}
                        onChange={e => {
                          setSearchProductor(e.target.value);
                          setSelectedProductor(null);
                          setShowSearchProductor(true);
                        }}
                        onFocus={() => setShowSearchProductor(true)}
                      />
                      {/* Lupa */}
                      <SearchOutlined style={{
                        position: 'absolute',
                        left: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        pointerEvents: 'none',
                        fontSize: '13px'
                      }} />
                      {/* Limpiar selección */}
                      {selectedProductor && (
                        <CloseOutlined
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#666',
                            cursor: 'pointer',
                            fontSize: '13px',
                            zIndex: 1100
                          }}
                          onClick={() => {
                            setSelectedProductor(null);
                            setSearchProductor('');
                            setShowSearchProductor(true);
                            form.setFieldValue('productor', undefined);
                          }}
                          title="Limpiar selección"
                        />
                      )}
                      {/* Dropdown */}
                      {showSearchProductor && productores.length > 0 && !selectedProductor && productorHabilitado && (
                        <div 
                          className="search-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '400px',
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          {productores.filter(p => {
                            const search = searchProductor.trim().toLowerCase();
                            return (
                              p.nc.toLowerCase().includes(search) ||
                              p.denominacion.toLowerCase().includes(search)
                            );
                          }).map(productor => (
                            <div
                              key={productor.id}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                textAlign: 'left'
                              }}
                              onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                              onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                              onClick={() => {
                                const personaProductor: Persona = {
                                  id_padron: productor.id,
                                  nc: productor.nc,
                                  denominacion: productor.denominacion
                                };
                                setSelectedProductor(personaProductor);
                                form.setFieldValue('productor', productor.id);
                                setShowSearchProductor(false);
                                handleProductorChange(productor.id);
                              }}
                            >
                              {productor.nc} - {productor.denominacion}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Establecimiento */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.productor !== currentValues.productor
              }
            >
              {({ getFieldValue }) => {
                const productorSeleccionado = getFieldValue('productor');
                const establecimientoHabilitado = !!productorSeleccionado;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Establecimiento:
                    </label>
                    <Form.Item
                      name="establecimiento"
                      rules={[{ required: establecimientoHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <Select 
                        placeholder={establecimientoHabilitado ? "Seleccione establecimiento" : "No disponible"}
                        size="middle"
                        showSearch
                        disabled={!establecimientoHabilitado}
                        onChange={handleEstablecimientoChange}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                      {establecimientoHabilitado && establecimientos.map(establecimiento => (
                        <Option key={establecimiento.id} value={establecimiento.id}>
                          {establecimiento.descripcion}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Lote */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.establecimiento !== currentValues.establecimiento
              }
            >
              {({ getFieldValue }) => {
                const establecimientoSeleccionado = getFieldValue('establecimiento');
                const loteHabilitado = !!establecimientoSeleccionado;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Lote:
                    </label>
                    <Form.Item
                      name="lote"
                      rules={[{ required: loteHabilitado, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <Select 
                        placeholder={loteHabilitado ? "Seleccione lote" : "No disponible"}
                        size="middle"
                        showSearch
                        disabled={!loteHabilitado}
                        optionFilterProp="children"
                        filterOption={(input: string, option?: { children: string; value: number }) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                      >
                      {loteHabilitado && lotes.map(lote => (
                        <Option key={lote.id} value={lote.id}>
                          {lote.descripcion}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Transportista */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Transportista:
              </label>
              <Form.Item
                name="transportista"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type="text"
                  placeholder="Buscar por NC o denominación..."
                  required
                  style={{
                    width: '100%',
                    paddingLeft: '30px',
                    paddingRight: selectedTransportista ? '30px' : undefined,
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    fontSize: '13px',
                    height: '32px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    backgroundColor: selectedTransportista ? '#f5f5f5' : 'white',
                    cursor: 'pointer'
                  }}
                  value={selectedTransportista ? `${selectedTransportista.nc} - ${selectedTransportista.denominacion}` : searchTransportista}
                  onChange={e => {
                    setSearchTransportista(e.target.value);
                    setSelectedTransportista(null);
                    setShowSearchTransportista(true);
                  }}
                  onFocus={() => setShowSearchTransportista(true)}
                />
                {/* Lupa */}
                <SearchOutlined style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  pointerEvents: 'none',
                  fontSize: '16px'
                }} />
                {/* Limpiar selección */}
                {selectedTransportista && (
                  <CloseOutlined
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#666',
                      cursor: 'pointer',
                      fontSize: '16px',
                      zIndex: 1100
                    }}
                    onClick={() => {
                      setSelectedTransportista(null);
                      setSearchTransportista('');
                      setShowSearchTransportista(true);
                      form.setFieldValue('transportista', undefined);
                    }}
                    title="Limpiar selección"
                  />
                )}
                {/* Dropdown */}
                {showSearchTransportista && transportistas.length > 0 && !selectedTransportista && (
                  <div 
                    className="search-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: '400px',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {transportistas.filter(t => {
                      const search = searchTransportista.trim().toLowerCase();
                      return (
                        t.nc.toLowerCase().includes(search) ||
                        t.denominacion.toLowerCase().includes(search)
                      );
                    }).map(transportista => (
                                              <div
                          key={transportista.id}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee',
                            textAlign: 'left'
                          }}
                        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                        onClick={() => {
                          const personaTransportista: Persona = {
                            id_padron: transportista.id,
                            nc: transportista.nc,
                            denominacion: transportista.denominacion
                          };
                          setSelectedTransportista(personaTransportista);
                          form.setFieldValue('transportista', transportista.id);
                          setShowSearchTransportista(false);
                          handleTransportistaChange(transportista.id);
                        }}
                      >
                        {transportista.nc} - {transportista.denominacion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </Form.Item>
            </div>

          {/* Chofer */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Chofer:
              </label>
              <Form.Item
                name="chofer"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <Select 
                  placeholder="Seleccione chofer"
                  size="middle"
                  showSearch
                  optionFilterProp="children"
                  disabled={!form.getFieldValue('transportista')}
                  filterOption={(input: string, option?: { children: string; value: number }) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                {choferes.map(chofer => (
                  <Option key={chofer.id} value={chofer.id}>
                    {chofer.nombre}
                  </Option>
                ))}
                </Select>
              </Form.Item>
            </div>

          {/* Camión/Acoplado */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Camión/Acoplado:
              </label>
              <Form.Item
                name="camion"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <Select 
                  placeholder="Seleccione camión"
                  size="middle"
                  showSearch
                  optionFilterProp="children"
                  disabled={!form.getFieldValue('transportista')}
                  filterOption={(input: string, option?: { children: string; value: number }) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                {camiones.map(camion => (
                  <Option key={camion.id} value={camion.id}>
                    {camion.patente_camion}/{camion.patente_acoplado}
                  </Option>
                ))}
                </Select>
              </Form.Item>
            </div>

          {/* Tarifa $/Km */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Tarifa $/Km:
              </label>
              <Form.Item
                name="tarifa"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <InputNumber<number>
                  placeholder="Ingrese tarifa"
                  size="middle"
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  formatter={(value: number | undefined) => 
                    value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
                  }
                  parser={(value: string | undefined): number => {
                    if (!value) return 0;
                    const parsed = value.replace(/\$\s?|(,*)/g, '');
                    return isNaN(Number(parsed)) ? 0 : Number(parsed);
                  }}
                />
              </Form.Item>
            </div>

          {/* Humedad */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.producto !== currentValues.producto
              }
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find(p => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Humedad:
                    </label>
                    <Form.Item
                      name="humedad"
                      rules={[{ required: camposHabilitados, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <InputNumber<number>
                        placeholder={camposHabilitados ? "Ingrese humedad" : "No disponible"}
                        size="middle"
                        disabled={!camposHabilitados}
                        style={{ width: '100%' }}
                        min={0}
                        step={0.01}
                        precision={2}
                        formatter={(value: number | undefined, info: { userTyping: boolean; input: string }) => 
                          value ? `${value}%` : ''
                        }
                        parser={(value: string | undefined): number => {
                          if (!value) return 0;
                          const parsed = value.replace('%', '');
                          return isNaN(Number(parsed)) ? 0 : Number(parsed);
                        }}
                      />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Remito */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                Remito:
              </label>
              <Form.Item
                name="remito"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <Input 
                  placeholder="Ingrese remito"
                  size="middle"
                  maxLength={13}
                  showCount
                />
              </Form.Item>
            </div>

          {/* Cantidad Rollos/Módulos */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.producto !== currentValues.producto
              }
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find(p => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Cantidad Rollos/Módulos:
                    </label>
                    <Form.Item
                      name="cantidad_rollos"
                      rules={[{ required: camposHabilitados, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <InputNumber<number>
                        placeholder={camposHabilitados ? "Ingrese cantidad" : "No disponible"}
                        size="middle"
                        disabled={!camposHabilitados}
                        style={{ width: '100%' }}
                        min={0}
                        precision={0}
                        formatter={(value: number | undefined, info: { userTyping: boolean; input: string }) => 
                          value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
                        }
                        parser={(value: string | undefined): number => {
                          if (!value) return 0;
                          const parsed = value.replace(/,/g, '');
                          return isNaN(Number(parsed)) ? 0 : Number(parsed);
                        }}
                      />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Ubicación en Playa */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.producto !== currentValues.producto
              }
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find(p => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;
                const playaHabilitada = verificarEstadoPlaya();

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Ubicación en Playa:
                    </label>
                    <Form.Item
                      name="ubicacion_playa"
                      rules={[{ required: camposHabilitados && playaHabilitada, message: 'Campo requerido' }]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <Input 
                        placeholder={
                          !camposHabilitados ? "No disponible" :
                          !playaHabilitada ? "Playa deshabilitada" :
                          "Ubicación asignada automáticamente"
                        }
                        size="middle"
                        disabled={true} // Siempre deshabilitado ya que se asigna automáticamente
                        maxLength={50}
                        showCount
                      />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* Calificación */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.producto !== currentValues.producto
              }
            >
              {({ getFieldValue }) => {
                const productoSeleccionado = productos.find(p => p.id === getFieldValue('producto'));
                const camposHabilitados = productoSeleccionado?.tipo === 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ 
                      fontWeight: 600, 
                      color: theme.colors.textPrimary,
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      marginBottom: 0,
                      lineHeight: '1.2'
                    }}>
                      Calificación:
                    </label>
                    <Form.Item
                      name="calificacion"
                      rules={[
                        { 
                          required: camposHabilitados && calificacionEnabled, 
                          message: 'Campo requerido' 
                        },
                        {
                          pattern: /^[1-5]{3}$/,
                          message: 'Debe ingresar exactamente 3 dígitos (solo números del 1 al 5)',
                          warningOnly: !calificacionEnabled // Si no está habilitado, solo muestra warning
                        }
                      ]}
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <Input
                        disabled={!calificacionEnabled}
                        placeholder={camposHabilitados ? "Ingrese calificación (3 números del 1 al 5)" : "No disponible"}
                        size="middle"
                        style={{ width: '100%' }}
                        maxLength={3}
                        onChange={(e) => {
                          // Solo permitir números del 1 al 5
                          const value = e.target.value.replace(/[^1-5]/g, '');
                          form.setFieldValue('calificacion', value);
                        }}
                      />
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>

          {/* 1ra Pesada */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 600, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0,
                lineHeight: '1.2'
              }}>
                1ra Pesada:
              </label>
              <Form.Item
                name="primera_pasada"
                rules={[{ required: true, message: 'Campo requerido' }]}
                style={{ marginBottom: 0, width: '100%' }}
              >
                <InputNumber<number>
                  placeholder="Ingrese peso"
                  size="middle"
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  formatter={(value: number | undefined, info: { userTyping: boolean; input: string }) => 
                    value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
                  }
                  parser={(value: string | undefined): number => {
                    if (!value) return 0;
                    const parsed = value.replace(/,/g, '');
                    return isNaN(Number(parsed)) ? 0 : Number(parsed);
                  }}
                />
              </Form.Item>
            </div>

        </div>

        {/* Botones de acción */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '8px',
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${theme.colors.borderLight}`,
          flexWrap: 'wrap'
        }}>
          <Button
            onClick={() => navigate('/listar_pesaje')}
            size="middle"
            style={{
              fontWeight: 500,
              minWidth: '100px',
              height: '32px',
              fontSize: '13px'
            }}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            size="middle"
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '100px',
              height: '32px',
              fontSize: '13px'
            }}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button
            onClick={() => {
              form.resetFields();
              // Limpiar los estados
              setChoferes([]);
              setCamiones([]);
              setSucursales([]);
              setEstablecimientos([]);
              setLotes([]);
              setTiposCosecha([]);
              setTiposSemilla([]);
              setPresentaciones([]);
              // Limpiar selecciones de personas
              setSelectedCliente(null);
              setSearchCliente('');
              setSelectedProductor(null);
              setSearchProductor('');
              setSelectedTransportista(null);
              setSearchTransportista('');
            }}
            size="middle"
            style={{
              fontWeight: 500,
              minWidth: '100px',
              height: '32px',
              fontSize: '13px'
            }}
            disabled={isSubmitting}
          >
            Nuevo
          </Button>
        </div>

        {/* Agregar overlay de carga */}
        {isSubmitting && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <Spin size="large" tip="Guardando pesaje..." />
          </div>
        )}
      </Form>
      </Card>
      </div>
    </>
  );
};

export default Pesaje;